/** Per-row preview of a proposed replacement: outcome counts, then diffs.
 *
 * Rows that will NOT change are shown, not hidden. A field the operator expected
 * to change but that reports `no_change` — or `stale`, or a rejection — is the
 * most useful thing on this panel, and filtering it out would turn a caught
 * mistake into an unnoticed one.
 */
import { Box, Chip, Stack, Typography } from '@mui/material';

import {
  OUTCOME_LABELS,
  OUTCOME_SEVERITY,
  type ReplaceHunk,
  type ReplaceOutcome,
  type ReplaceOutcomeCode,
} from '@/api/contentSearch';

import { RecordLink } from './RecordLink';

const MONO = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 12,
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
};

export function OutcomeSummary({
  summary,
}: {
  summary: Partial<Record<ReplaceOutcomeCode, number>>;
}) {
  const entries = Object.entries(summary) as [ReplaceOutcomeCode, number][];
  if (entries.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
      {entries.map(([code, count]) => (
        <Chip
          key={code}
          size="small"
          color={OUTCOME_SEVERITY[code] ?? 'default'}
          variant={code === 'will_change' || code === 'applied' ? 'filled' : 'outlined'}
          label={`${OUTCOME_LABELS[code] ?? code}: ${count.toLocaleString()}`}
        />
      ))}
    </Stack>
  );
}

function Hunk({ hunk }: { hunk: ReplaceHunk }) {
  return (
    <Box sx={{ ...MONO, py: 0.5 }}>
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {hunk.context_before}
      </Box>
      <Box
        component="del"
        sx={{
          bgcolor: 'error.light',
          color: 'error.contrastText',
          textDecoration: 'line-through',
          px: 0.25,
          borderRadius: 0.5,
        }}
      >
        {hunk.old}
      </Box>
      <Box
        component="ins"
        sx={{
          bgcolor: 'success.light',
          color: 'success.contrastText',
          textDecoration: 'none',
          px: 0.25,
          ml: 0.25,
          borderRadius: 0.5,
        }}
      >
        {hunk.new}
      </Box>
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {hunk.context_after}
      </Box>
    </Box>
  );
}

export function PreviewRows({ rows }: { rows: ReplaceOutcome[] }) {
  if (rows.length === 0) return null;
  return (
    <Stack spacing={1.5} sx={{ mt: 1, maxHeight: 340, overflowY: 'auto' }}>
      {rows.map((row) => (
        <Box
          key={row.id}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25, minWidth: 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip
              size="small"
              color={OUTCOME_SEVERITY[row.outcome] ?? 'default'}
              variant="outlined"
              label={OUTCOME_LABELS[row.outcome] ?? row.outcome}
            />
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} component="div">
              <RecordLink link={row.link} label={row.label} kindLabel={row.kind_label} />
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {' · '}
                {row.slot_label}
              </Box>
            </Typography>
            {row.replacements > 0 && (
              <Typography variant="caption" color="text.secondary">
                {row.replacements} change(s)
              </Typography>
            )}
          </Stack>
          {row.detail && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              {row.detail}
            </Typography>
          )}
          {row.hunks.map((h, i) => (
            <Hunk key={`${row.id}-${h.offset}-${i}`} hunk={h} />
          ))}
          {row.hunks_truncated && (
            <Typography variant="caption" color="text.secondary">
              … more changes in this field than shown
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}
